/**
 * The frame of the sharing sheets (handoff §17): the owner's share sheet and the reader's
 * save sheet. A bottom sheet on a phone, a centred 440 px dialog on desktop, as the
 * player's confirmations (`PlayerSheet`). Mounted only while open; Esc and the scrim close
 * it. It joins the swipe block (ADR-106 P3).
 */
import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { useIsDesktop } from '../../../hooks/useIsDesktop'
import { useSwipeNavBlock } from '../../../hooks/useSwipeNavBlock'
import { CONTROL_SURFACE, suppressTouchContextMenu } from '../../../utils/controlSurface'

export default function SheetFrame({ title, onClose, closeLabel, children, testId, showClose = false }: {
  title: string
  onClose: () => void
  closeLabel: string
  children: ReactNode
  testId?: string
  /** The title's ×: on the share sheet once a link exists (§17.1). */
  showClose?: boolean
}) {
  const desktop = useIsDesktop()
  useSwipeNavBlock(true, 'text-share-sheet')
  const closeRef = useRef(onClose)
  useEffect(() => { closeRef.current = onClose })
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return
      e.preventDefault()
      closeRef.current()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const dialog = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="text-share-sheet-title"
      data-testid={testId}
      onClick={(e) => e.stopPropagation()}
      style={desktop ? {
        background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 16, boxShadow: 'var(--shadow-float)',
        padding: 24, width: '100%', maxWidth: 440, boxSizing: 'border-box', fontFamily: 'var(--font-ui)',
      } : {
        position: 'absolute', left: 0, right: 0, bottom: 0, maxHeight: '90dvh', overflowY: 'auto', boxSizing: 'border-box',
        background: 'var(--bg-surface)', borderRadius: '16px 16px 0 0', boxShadow: 'var(--shadow-float)',
        fontFamily: 'var(--font-ui)', padding: '8px 20px max(20px, env(safe-area-inset-bottom))',
      }}
    >
      {!desktop && <div aria-hidden style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--border)', margin: '0 auto 16px' }} />}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
        <h2 id="text-share-sheet-title" style={{ flex: 1, margin: 0, fontSize: 20, fontWeight: 700, lineHeight: 1.3, color: 'var(--text-primary)' }}>{title}</h2>
        {showClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label={closeLabel}
            data-testid="share-sheet-close"
            style={{
              width: 36, height: 36, margin: '-6px -8px 0 0', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)',
            }}
          >
            <X size={20} />
          </button>
        )}
      </div>
      {children}
    </div>
  )
  return createPortal(
    <div
      className={`add-sheet-scrim ${CONTROL_SURFACE}`}
      onContextMenu={suppressTouchContextMenu}
      onClick={() => closeRef.current()}
      style={desktop
        ? { position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }
        : { position: 'fixed', inset: 0, zIndex: 200 }}
    >
      {dialog}
    </div>,
    document.body,
  )
}

/** A full-width pair of sheet actions: stacked on a phone (primary first), end-aligned on desktop. */
export function SheetActions({ children }: { children: ReactNode }) {
  const desktop = useIsDesktop()
  return (
    <div style={desktop
      ? { display: 'flex', flexDirection: 'row-reverse', justifyContent: 'flex-start', gap: 10, marginTop: 24 }
      : { display: 'flex', flexDirection: 'column', gap: 8, marginTop: 20 }}
    >
      {children}
    </div>
  )
}
